import { ArrowRight, CheckCircle, MessageSquare, Pause, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from './Layout';
import { useConfig } from '../hooks/useConfig';

const DemoPage = () => {
  const { site } = useConfig();
  const [currentMessage, setCurrentMessage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const whatsappDemo = [
    { type: 'customer', message: "Hi! I'd like to book a haircut for tomorrow", time: "2:30 PM" },
    { type: 'bot', message: "Hello! I'd be happy to help you book a haircut. What time works best for you tomorrow?", time: "2:30 PM" },
    { type: 'customer', message: "Is 2:00 PM available?", time: "2:31 PM" },
    { type: 'bot', message: "Let me check our availability... Yes! I have 2:00 PM available with Sarah. A standard haircut takes about 30 minutes. Shall I book that for you?", time: "2:31 PM" },
    { type: 'customer', message: "Perfect! My name is John Smith", time: "2:31 PM" },
    { type: 'bot', message: "Excellent! I've booked your haircut appointment:\n\n📅 Date: Tomorrow (March 15th)\n⏰ Time: 2:00 PM - 2:30 PM\n💇‍♂️ Service: Haircut with Sarah\n👤 Client: John Smith\n\nYou'll receive a reminder 1 hour before your appointment. Is there anything else I can help you with?", time: "2:32 PM" },
    { type: 'customer', message: "That's all, thank you!", time: "2:32 PM" },
    { type: 'bot', message: "You're welcome! Looking forward to seeing you tomorrow at 2:00 PM. Have a great day! ✂️", time: "2:32 PM" }
  ];

  const currentDemoMessages = whatsappDemo;

  useEffect(() => {
    let interval;
    if (isPlaying && currentMessage < currentDemoMessages.length - 1) {
      interval = setInterval(() => {
        setCurrentMessage(prev => prev + 1);
      }, 2000);
    } else if (currentMessage >= currentDemoMessages.length - 1) {
      setIsPlaying(false);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentMessage, currentDemoMessages.length]);

  const resetDemo = () => {
    setCurrentMessage(0);
    setIsPlaying(false);
  };

  const toggleDemo = () => {
    if (currentMessage >= currentDemoMessages.length - 1) {
      resetDemo();
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            See Your AI Assistant in Action
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            Watch how our AI handles real customer interactions through WhatsApp and phone calls
          </p>
        </div>
      </section>

      {/* Interactive Demo */}
      <section id="demo" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Interactive Demo</h2>
            <p className="mt-4 text-lg text-gray-600">
              Experience how customers interact with your AI booking assistant
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Demo Interface */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <MessageSquare className="h-6 w-6 text-green-500" />
                  <span className="ml-2 font-medium">WhatsApp Business</span>
                </div>
                <button
                  onClick={toggleDemo}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {isPlaying ? (
                    <>
                      <Pause className="mr-2 h-4 w-4" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      {currentMessage >= currentDemoMessages.length - 1 ? 'Restart' : 'Play'}
                    </>
                  )}
                </button>
              </div>

              <div className="bg-white rounded-lg p-4 h-96 overflow-y-auto">
                {currentDemoMessages.slice(0, currentMessage + 1).map((msg, index) => (
                  <div key={index} className={`mb-4 ${msg.type === 'customer' ? 'text-right' : 'text-left'}`}>
                    <div className={`inline-block max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      msg.type === 'customer'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-900'
                    }`}>
                      <p className="text-sm whitespace-pre-line">{msg.message}</p>
                      <p className="text-xs mt-1 opacity-70">{msg.time}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>Message {currentMessage + 1} of {currentDemoMessages.length}</span>
                <button
                  onClick={resetDemo}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Reset Demo
                </button>
              </div>
            </div>

            {/* Demo Insights */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">What You're Seeing</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Natural Conversation</h4>
                      <p className="text-sm text-gray-600">AI understands context and responds naturally</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Real-time Availability</h4>
                      <p className="text-sm text-gray-600">Checks calendar instantly to prevent double bookings</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Automatic Confirmation</h4>
                      <p className="text-sm text-gray-600">Creates appointment and sends confirmation details</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Pro Tip</h4>
                <p className="text-sm text-blue-800">
                  Your customers can book 24/7 through WhatsApp, even when you're closed!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white">Ready to Try It for Your Business?</h2>
          <p className="mt-4 text-xl text-blue-100">
            Set up your AI booking assistant in under 5 minutes
          </p>
          <div className="mt-8">
            <Link to="/register" className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 flex items-center justify-center mx-auto">
              Start {site.pricing.trialDays}-Day Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <p className="mt-4 text-blue-100">
              No credit card required • Setup in minutes
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DemoPage;
